import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { Eye, EyeOff, Lock } from "react-feather";
import { useChangeMyPasswordMutation } from "../profileApi";
import { passwordChanged, selectAuth } from "../../auth/authSlice";
import { paths } from "../../../routes/paths";

// Web twin of the Flutter app's Change Password screen (same three fields,
// same 6-character minimum the API enforces). Added 2026-09-10 - the web app
// had no way for anyone to change their own password; the dashboard banner
// even said "as soon as a change-password screen is available". Reached from
// the profile menu, and straight after sign-in when the account is on a
// temporary password an admin set (MustChangePassword).
const MIN_LENGTH = 6;

const PasswordField = ({ label, name, register, rules, error, autoComplete }) => {
  const [visible, setVisible] = useState(false);
  return (
    <div className="mb-3">
      <label className="form-label" htmlFor={name}>
        {label}
      </label>
      <div className="input-group">
        <input
          id={name}
          type={visible ? "text" : "password"}
          className={`form-control${error ? " is-invalid" : ""}`}
          autoComplete={autoComplete}
          {...register(name, rules)}
        />
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={() => setVisible((v) => !v)}
          aria-label={visible ? `Hide ${label}` : `Show ${label}`}
        >
          {visible ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
        {error && <div className="invalid-feedback d-block">{error.message}</div>}
      </div>
    </div>
  );
};

const ChangePasswordPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { mustChangePassword } = useSelector(selectAuth);
  const [changeMyPassword, { isLoading }] = useChangeMyPasswordMutation();
  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm({ defaultValues: { currentPassword: "", newPassword: "", confirmPassword: "" } });

  const onSubmit = async ({ currentPassword, newPassword }) => {
    try {
      await changeMyPassword({ currentPassword, newPassword }).unwrap();
      dispatch(passwordChanged());
      reset();
      toast.success("Password changed.");
      navigate(paths.dashboard, { replace: true });
    } catch (error) {
      // e.g. "Current password is incorrect." - the API's own wording.
      toast.error(error?.data?.message || error?.message || "Could not change the password.");
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="page-title">
            <h4>Change Password</h4>
            <h6>Change the password you sign in with, on the web and in the app</h6>
          </div>
        </div>

        <div className="card" style={{ maxWidth: 520 }}>
          <div className="card-body">
            {mustChangePassword && (
              <div className="alert alert-warning d-flex align-items-start gap-2" role="alert">
                <Lock size={16} className="mt-1 flex-shrink-0" />
                <span>
                  You are signed in with a temporary password. Choose your own password now - the
                  temporary one should not be kept.
                </span>
              </div>
            )}

            <form onSubmit={handleSubmit(onSubmit)} noValidate>
              <PasswordField
                label="Current password"
                name="currentPassword"
                autoComplete="current-password"
                register={register}
                error={errors.currentPassword}
                rules={{ required: "Enter your current password." }}
              />
              <PasswordField
                label="New password"
                name="newPassword"
                autoComplete="new-password"
                register={register}
                error={errors.newPassword}
                rules={{
                  required: "Enter a new password.",
                  minLength: { value: MIN_LENGTH, message: `Use at least ${MIN_LENGTH} characters.` },
                  validate: (v, all) =>
                    v !== all.currentPassword || "The new password must be different from the current one.",
                }}
              />
              <PasswordField
                label="Confirm new password"
                name="confirmPassword"
                autoComplete="new-password"
                register={register}
                error={errors.confirmPassword}
                rules={{
                  required: "Type the new password again.",
                  validate: (v) => v === watch("newPassword") || "The two new passwords do not match.",
                }}
              />
              <small className="text-muted d-block mb-3">
                At least {MIN_LENGTH} characters. This changes your password everywhere, including the
                mobile app.
              </small>
              <div className="d-flex gap-2">
                <button type="submit" className="btn btn-primary" disabled={isLoading}>
                  {isLoading ? "Changing..." : "Change Password"}
                </button>
                {!mustChangePassword && (
                  <button type="button" className="btn btn-outline-secondary" onClick={() => navigate(-1)}>
                    Cancel
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChangePasswordPage;
